/**
 * ws_jobs — well servicing. GET /dashboard/ws-jobs (api#1296, WS-6): open jobs,
 * in progress first, with their counts by status, tour sheets awaiting the
 * office, BOP attention and late starts. "Late", "BOP attention" and every
 * count are the API's; planned starts are calendar days.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { WidgetFigure, WidgetPanel } from '../WidgetPanel';
import { useWsJobsWidget } from '../overviewQueries';
import { VerticalOff, OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import { wsJobStatusSkin } from '../../../lib/well-servicing';
import { formatCalendarDate } from '../../../lib/calendar-date';
import type { WsJobs } from '../../../types/overview-operations';

function Body({ d }: { d: WsJobs }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <WidgetFigure testId="wsj-in-progress" label="In progress" value={d.counts.in_progress} />
        <WidgetFigure testId="wsj-sheets" label="Sheets to approve" value={d.counts.sheets_to_approve} alert={d.counts.sheets_to_approve > 0} />
        <WidgetFigure testId="wsj-bop" label="BOP attention" value={d.counts.bop_certs_attention} alert={d.counts.bop_certs_attention > 0} />
        <WidgetFigure testId="wsj-late" label="Late" value={d.counts.late} alert={d.counts.late > 0} />
      </div>
      {d.jobs.length === 0 ? (
        <p data-testid="wsj-empty" className="mt-4 text-sm text-fieldiq-muted">No open well-servicing jobs.</p>
      ) : (
        <div data-testid="wsj-jobs" className="mt-4 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.jobs.map((j) => {
            const skin = wsJobStatusSkin(j.status);
            return (
              <OpsRow key={j.id} to={j.link} onOpen={navigate} testId={`wsj-job-${j.id}`}>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-fieldiq-text">{dash(j.job_number)} · {dash(j.well_name)}</span>
                  <span className="block truncate text-fieldiq-muted">{dash(j.customer_name)} · {dash(j.rig_number)} · {dash(j.supervisor_name)}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="flex gap-1">
                    <OpsBadge fill={skin.fill}>{skin.label}</OpsBadge>
                    {typeof j.sheets_to_approve === 'number' && j.sheets_to_approve > 0 && (
                      <OpsBadge fill="bg-fi-signal-blue" testId="wsj-sheets-badge">{`${j.sheets_to_approve} sheet${j.sheets_to_approve === 1 ? '' : 's'}`}</OpsBadge>
                    )}
                    {j.bop_attention && <OpsBadge fill="bg-fi-signal-amber" testId="wsj-bop-badge">BOP</OpsBadge>}
                    {j.late && <OpsBadge fill="bg-fi-signal-red" testId="wsj-late-badge">Late</OpsBadge>}
                  </span>
                  <span className="tabular-nums text-fieldiq-text">{formatCalendarDate(j.planned_start_on, { month: 'short', day: 'numeric' }) ?? '—'}</span>
                </span>
              </OpsRow>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function WsJobsWidget() {
  const { hasVertical } = useAuth();
  const on = hasVertical('well_servicing');
  const q = useWsJobsWidget(on);
  return (
    <WidgetPanel widgetKey="ws_jobs" eyebrow="Well servicing" title="Well-Servicing Jobs" link={on ? { label: 'Jobs', to: '/well-servicing/jobs' } : undefined}>
      <div className="p-4">
        {!on ? (
          <VerticalOff testId="wsj-vertical-off">Well servicing is not on for this company.</VerticalOff>
        ) : (
          <OpsWidgetRead q={q} subject="well-servicing jobs" allowed="admins, office staff, supervisors, dispatchers and logistics">
            {(d) => <Body d={d} />}
          </OpsWidgetRead>
        )}
      </div>
    </WidgetPanel>
  );
}
